import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader as LoaderIcon, Send as SendIcon } from "lucide-react";
import { isTemplateNameValid } from "../../template-editor-presentation.js";
import { AnimatePresence, motion, useReducedMotion } from "../../../../lib/motion.js";
import { Button } from "../../../../components/ui/button/index.js";
import {
  canProceedFromDefinition,
  createInitialWizardState,
  defaultUseCaseForCategory,
  type TemplateCreationCategory,
  type TemplateCreationUseCase,
  type TemplateCreationWizardState,
} from "../template-creation-wizard-state.js";
import {
  createEmptyTemplateFormState,
  isTemplateEditorDraftDirty,
  type TemplateFormState,
} from "../template-management-form.js";
import { TemplateUnsavedChangesDialog } from "../TemplateUnsavedChangesDialog.js";
import { canSubmitMarketingDefaultTemplate } from "../../template-editor-presentation.js";
import { mapTemplateManagementError } from "../template-management-errors.js";
import { useWhatsAppTemplateHeaderImageUrl } from "../../use-whatsapp-template-header-image.js";
import {
  canAdvanceCreateEditToReview,
  nextCreateWizardStep,
  previousCreateWizardStep,
} from "./template-creation-review.js";
import { TemplateDefinitionStep } from "./TemplateDefinitionStep.js";
import { TemplateEditorStep } from "./TemplateEditorStep.js";
import { TemplateReviewStep } from "./TemplateReviewStep.js";
import {
  templateWizardStepMotion,
  type TemplateWizardStepDirection,
} from "./template-creation-wizard-motion.js";
import { WizardStepIndicator } from "./WizardStepIndicator.js";

export interface TemplateCreationWizardProps {
  onClose: () => void;
  onSubmit: (formState: TemplateFormState) => Promise<void>;
  callingManagerUrl?: string | null;
  unreachableMessage: string;
  /** Create starts at Step 1; edit opens Step 2 with hydrated form state. */
  mode?: "create" | "edit";
  initialFormState?: TemplateFormState;
  templateStatusLabel?: string | null;
  submitLabelKey?: string;
  organizationId?: number;
  templateId?: number | null;
}

function isFormDirty(state: TemplateFormState): boolean {
  return !!(
    state.name ||
    state.bodyText ||
    state.headerText ||
    state.footerText ||
    state.buttons.length > 0
  );
}

function createEditWizardState(
  formState: TemplateFormState,
): TemplateCreationWizardState {
  const category: TemplateCreationCategory =
    formState.category === "UTILITY" ? "UTILITY" : "MARKETING";
  return {
    step: "EDIT",
    category,
    useCase: category === "UTILITY" ? "UTILITY_DEFAULT" : "MARKETING_DEFAULT",
  };
}

export function TemplateCreationWizard({
  onClose,
  onSubmit,
  callingManagerUrl = null,
  unreachableMessage,
  mode = "create",
  initialFormState,
  templateStatusLabel = null,
  submitLabelKey,
  organizationId,
  templateId = null,
}: TemplateCreationWizardProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const reduceMotion = useReducedMotion();
  const isRtl = i18n.dir() === "rtl";
  const isEditMode = mode === "edit";
  const [stepDirection, setStepDirection] = useState<TemplateWizardStepDirection>(1);
  const [wizardState, setWizardState] = useState<TemplateCreationWizardState>(() => {
    if (isEditMode && initialFormState) return createEditWizardState(initialFormState);
    if (initialFormState) {
      const category: TemplateCreationCategory =
        initialFormState.category === "UTILITY" ? "UTILITY" : "MARKETING";
      return {
        step: "DEFINITION",
        category,
        useCase: category === "UTILITY" ? "UTILITY_DEFAULT" : "MARKETING_DEFAULT",
      };
    }
    return createInitialWizardState();
  });
  const [formState, setFormState] = useState<TemplateFormState>(() =>
    initialFormState ?? createEmptyTemplateFormState(),
  );
  const [showBackConfirm, setShowBackConfirm] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const baselineFormRef = useRef(formState);
  const baselineWizardRef = useRef(wizardState);
  const editorDirty =
    isTemplateEditorDraftDirty(formState, baselineFormRef.current) ||
    wizardState.category !== baselineWizardRef.current.category ||
    wizardState.useCase !== baselineWizardRef.current.useCase;
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const submitInFlightRef = useRef(false);
  const storedHeaderUrl = useWhatsAppTemplateHeaderImageUrl(
    organizationId ?? null,
    templateId,
    isEditMode && templateId != null && initialFormState?.hasStoredHeaderImage === true,
  );

  useEffect(() => {
    if (!storedHeaderUrl) return;
    setFormState((previous) => {
      if (previous.headerKind !== "IMAGE") return previous;
      if (previous.headerImageFile) return previous;
      if (previous.headerImagePreviewUrl === storedHeaderUrl) return previous;
      return {
        ...previous,
        headerImagePreviewUrl: storedHeaderUrl,
        hasStoredHeaderImage: true,
      };
    });
  }, [storedHeaderUrl]);

  const canAdvanceFromDefinition =
    !isEditMode &&
    wizardState.step === "DEFINITION" &&
    canProceedFromDefinition(wizardState.category, wizardState.useCase) &&
    isTemplateNameValid(formState.name);

  const canContinueToReview =
    !isEditMode &&
    wizardState.step === "EDIT" &&
    canAdvanceCreateEditToReview(formState);

  const handleCategoryChange = useCallback(
    (category: TemplateCreationCategory) => {
      setWizardState((prev) => ({
        ...prev,
        category,
        useCase: defaultUseCaseForCategory(category),
      }));
    },
    [],
  );

  function setWizardStep(
    next: TemplateCreationWizardState["step"],
    direction: TemplateWizardStepDirection,
  ): void {
    setStepDirection(direction);
    setWizardState((prev) => ({ ...prev, step: next }));
  }

  function handleNextFromDefinition(): void {
    if (!canAdvanceFromDefinition) return;
    setSubmitError(null);
    setFormState((prev) => ({
      ...prev,
      category: wizardState.category === "UTILITY" ? "UTILITY" : "MARKETING",
      parameterFormat: "NAMED",
    }));
    const next = nextCreateWizardStep("DEFINITION", { editValid: true });
    if (next) setWizardStep(next, 1);
  }

  function handleContinueToReview(): void {
    if (isEditMode || wizardState.step !== "EDIT") return;
    if (!canAdvanceCreateEditToReview(formState)) return;
    setSubmitError(null);
    const next = nextCreateWizardStep("EDIT", { editValid: true });
    if (next) setWizardStep(next, 1);
  }

  function requestClose(): void {
    if (submitting) return;
    if (editorDirty) {
      setShowCloseConfirm(true);
      return;
    }
    onClose();
  }

  function handleBack(): void {
    if (isEditMode) return;
    const previous = previousCreateWizardStep(wizardState.step);
    if (!previous) return;

    if (wizardState.step === "EDIT" && previous === "DEFINITION") {
      if (isFormDirty(formState)) {
        setShowBackConfirm(true);
        return;
      }
      setSubmitError(null);
      setWizardStep(previous, -1);
      return;
    }

    // REVIEW → EDIT: preserve all form state (no reset, no confirm).
    setSubmitError(null);
    setWizardStep(previous, -1);
  }

  function confirmBack(): void {
    setShowBackConfirm(false);
    setSubmitError(null);
    setFormState(createEmptyTemplateFormState());
    setWizardStep("DEFINITION", -1);
  }

  async function handleSubmit(): Promise<void> {
    if (submitInFlightRef.current || submitting) return;
    if (!canSubmitMarketingDefaultTemplate(formState)) {
      // Provider POST must never rely only on navigation state.
      if (!isEditMode && wizardState.step === "REVIEW") {
        setWizardStep("EDIT", -1);
      }
      return;
    }
    submitInFlightRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmit(formState);
      if (!isEditMode) {
        setFormState(createEmptyTemplateFormState());
        setWizardState(createInitialWizardState());
      }
    } catch (error) {
      setSubmitError(mapTemplateManagementError(t, error, unreachableMessage));
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  }

  const editorCategory: TemplateCreationCategory =
    wizardState.category ??
    (formState.category === "UTILITY" ? "UTILITY" : "MARKETING");
  const editorUseCase: TemplateCreationUseCase =
    wizardState.useCase ?? "MARKETING_DEFAULT";

  const resolvedSubmitLabelKey =
    submitLabelKey ??
    (isEditMode
      ? "whatsapp.templates.manage.submitEdit"
      : "whatsapp.templates.manage.wizard.steps.review");

  const showCreateBack =
    !isEditMode &&
    (wizardState.step === "EDIT" || wizardState.step === "REVIEW");

  const stepMotion = templateWizardStepMotion(stepDirection, {
    isRtl,
    reduceMotion: reduceMotion ?? false,
  });

  return (
    <div className="flex h-full min-h-0 flex-col">
      {!isEditMode ? (
        <header className="shrink-0 border-b border-line px-4 py-3 md:px-6 md:py-4">
          <WizardStepIndicator currentStep={wizardState.step} />
        </header>
      ) : (
        <header className="shrink-0 border-b border-line px-4 py-3.5 md:px-6 md:py-4">
          <div className="grid gap-1">
            <h2 className="m-0 text-base font-semibold tracking-[-0.015em] text-ink">
              {t("whatsapp.templates.manage.editTitle")}
            </h2>
            <p className="m-0 text-[0.8125rem] leading-snug text-ink-muted">
              {t("whatsapp.templates.manage.editDescription")}
            </p>
          </div>
        </header>
      )}

      {submitError ? (
        <div
          className="shrink-0 border-b border-error/20 bg-error-muted px-4 py-3 text-sm text-error md:px-6"
          role="alert"
        >
          {submitError}
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain px-4 py-6 md:px-6 md:py-8">
        {isEditMode ? (
          <TemplateEditorStep
            mode="edit"
            category={editorCategory}
            useCase={editorUseCase}
            formState={formState}
            templateStatusLabel={templateStatusLabel}
            onFormChange={(next) => {
              setSubmitError(null);
              setFormState(next);
            }}
            callingManagerUrl={callingManagerUrl}
          />
        ) : (
          <AnimatePresence mode="wait" initial={false}>
            {wizardState.step === "DEFINITION" ? (
              <motion.div
                key="wizard-step-definition"
                variants={stepMotion.variants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={stepMotion.transition}
                className="w-full min-w-0"
              >
                <TemplateDefinitionStep
                  category={wizardState.category}
                  formState={formState}
                  onCategoryChange={handleCategoryChange}
                  onFormChange={(next) => {
                    setSubmitError(null);
                    setFormState(next);
                  }}
                />
              </motion.div>
            ) : null}

            {wizardState.step === "EDIT" ? (
              <motion.div
                key="wizard-step-edit"
                variants={stepMotion.variants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={stepMotion.transition}
                className="w-full min-w-0"
              >
                <TemplateEditorStep
                  mode="create"
                  category={editorCategory}
                  useCase={editorUseCase}
                  formState={formState}
                  templateStatusLabel={templateStatusLabel}
                  onFormChange={(next) => {
                    setSubmitError(null);
                    setFormState(next);
                  }}
                  callingManagerUrl={callingManagerUrl}
                />
              </motion.div>
            ) : null}

            {wizardState.step === "REVIEW" ? (
              <motion.div
                key="wizard-step-review"
                variants={stepMotion.variants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={stepMotion.transition}
                className="w-full min-w-0"
              >
                <TemplateReviewStep
                  category={editorCategory}
                  useCase={editorUseCase}
                  formState={formState}
                />
              </motion.div>
            ) : null}
          </AnimatePresence>
        )}
      </div>

      <div className="flex shrink-0 items-center justify-between gap-2 border-t border-line bg-surface-elevated px-4 py-3 shadow-[0_-8px_20px_rgb(15_23_42/0.04)] pb-[max(0.75rem,env(safe-area-inset-bottom))] md:px-6">
        <div>
          {showCreateBack ? (
            <Button
              type="button"
              variant="ghost"
              onClick={handleBack}
              disabled={submitting}
              className="gap-1.5"
            >
              <ArrowLeft size={16} className="rtl:rotate-180" aria-hidden="true" />
              {t("whatsapp.templates.manage.wizard.editor.previous")}
            </Button>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant={!isEditMode && wizardState.step === "REVIEW" ? "ghost" : "outline"}
            onClick={requestClose}
            disabled={submitting}
          >
            {t("whatsapp.templates.manage.wizard.close")}
          </Button>
          {!isEditMode && wizardState.step === "DEFINITION" && (
            <Button
              type="button"
              disabled={!canAdvanceFromDefinition || submitting}
              onClick={handleNextFromDefinition}
            >
              {t("whatsapp.templates.manage.wizard.next")}
            </Button>
          )}
          {!isEditMode && wizardState.step === "EDIT" && (
            <Button
              type="button"
              onClick={handleContinueToReview}
              disabled={submitting || !canContinueToReview}
            >
              {t("whatsapp.templates.manage.wizard.next")}
            </Button>
          )}
          {isEditMode && wizardState.step === "EDIT" && (
            <Button
              type="button"
              onClick={() => {
                void handleSubmit();
              }}
              disabled={
                submitting || !canSubmitMarketingDefaultTemplate(formState)
              }
            >
              {submitting
                ? t("whatsapp.templates.manage.saving")
                : t(resolvedSubmitLabelKey)}
            </Button>
          )}
          {!isEditMode && wizardState.step === "REVIEW" && (
            <Button
              type="button"
              className="gap-2 px-5 shadow-md"
              onClick={() => {
                void handleSubmit();
              }}
              disabled={
                submitting || !canSubmitMarketingDefaultTemplate(formState)
              }
            >
              {submitting ? (
                <LoaderIcon size={16} className="animate-spin" aria-hidden="true" />
              ) : (
                <SendIcon size={16} className="rtl:-scale-x-100" aria-hidden="true" />
              )}
              {submitting
                ? t("whatsapp.templates.manage.saving")
                : t(resolvedSubmitLabelKey)}
            </Button>
          )}
        </div>
      </div>

      <TemplateUnsavedChangesDialog
        open={showCloseConfirm}
        onStay={() => setShowCloseConfirm(false)}
        onDiscard={() => {
          setShowCloseConfirm(false);
          onClose();
        }}
      />

      {showBackConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="mx-4 w-full max-w-sm rounded-lg bg-surface p-6 shadow-xl">
            <h4 className="mb-2 text-sm font-semibold text-ink">
              {t("whatsapp.templates.manage.wizard.editor.backConfirmTitle")}
            </h4>
            <p className="mb-5 text-sm text-ink-muted">
              {t("whatsapp.templates.manage.wizard.editor.backConfirmBody")}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowBackConfirm(false)}
              >
                {t("whatsapp.templates.manage.wizard.editor.backConfirmContinue")}
              </Button>
              <Button type="button" size="sm" onClick={confirmBack}>
                {t("whatsapp.templates.manage.wizard.editor.backConfirmBack")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
